import { useCallback, useEffect, useMemo } from 'react'
import { Logger } from '@axiomhq/logging'
import { useLogger } from '@/lib/axiom/client'
import { useMbmUserId } from '@/providers/MbmUserProvider'

export type MbmLogger = Logger & {
  flushLogs: () => Promise<void>
}

/**
 * Sends any buffered client logs to Axiom immediately.
 * Prefer the logger returned from {@link useMbmLogger} so the full logger tree is flushed.
 */
export async function flushClientLogs(logger?: MbmLogger | Logger) {
  if (!logger) return
  if ('flushLogs' in logger && typeof logger.flushLogs === 'function') {
    await logger.flushLogs()
    return
  }
  await logger.flush()
}

/**
 * Logs an info event and flushes immediately so payment/checkout events are not lost
 * when the Razorpay modal backgrounds the page or a redirect unmounts the component.
 */
export async function logClientInfoAndFlush(
  logger: MbmLogger | undefined,
  message: string,
  fields: Record<string, unknown> = {},
) {
  if (!logger) return
  logger.info(message, fields)
  await flushClientLogs(logger)
}

/**
 * Logs an error event and flushes immediately (e.g. payment.failed, Payment Error).
 */
export async function logClientErrorAndFlush(
  logger: MbmLogger | undefined,
  message: string,
  fields: Record<string, unknown> = {},
) {
  if (!logger) return
  logger.error(message, fields)
  await flushClientLogs(logger)
}

/**
 * Axiom logger scoped with the current `mbm-user-id` (reactive after MbmUserProvider mounts).
 * Flushes the full logger tree when the tab is hidden so UPI / payment flows still report to Axiom.
 */
export function useMbmLogger(): MbmLogger {
  const baseLogger = useLogger()
  const userId = useMbmUserId()

  const flushLogs = useCallback(async () => {
    await baseLogger.flush()
  }, [baseLogger])

  const logger = useMemo(() => {
    const scopedLogger = baseLogger.with({ userId: userId ?? undefined }) as MbmLogger
    scopedLogger.flushLogs = () => baseLogger.flush()
    return scopedLogger
  }, [baseLogger, userId, flushLogs])

  useEffect(() => {
    const flushOnPageHide = () => {
      void baseLogger.flush()
    }

    const flushOnVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        void baseLogger.flush()
      }
    }

    window.addEventListener('pagehide', flushOnPageHide)
    document.addEventListener('visibilitychange', flushOnVisibilityChange)

    return () => {
      window.removeEventListener('pagehide', flushOnPageHide)
      document.removeEventListener('visibilitychange', flushOnVisibilityChange)
    }
  }, [baseLogger])

  return logger
}
